import React from 'react';

import { PercentageOutlined } from '@ant-design/icons';
import { Table as AntTable, Button, InputNumber, Space, TableProps, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { ViewProductSchema } from '@app/models/products';

import { useLocalDispatch, useLocalSelector } from '@app/services/hooks/useRedux';
import { resetFilters } from '@app/store/productsReducer';
import { SearchControls } from '@components/products';

import { useGetCatalogProductsQuery } from '@pages/Catalogs/hooks';

import { ISelectedProductLimit } from '../../interfaces';

import { BASE_COLUMNS } from './constants';
import { Container } from './styles';

interface IProps {
  catalog_uid: string;
  onSelectionChange?: (uids: ISelectedProductLimit[]) => void;
  selectedProductUids: ISelectedProductLimit[];
}

export const ProductsTable: React.FC<IProps> = ({ catalog_uid, onSelectionChange, selectedProductUids }) => {
  const [y, setY] = React.useState<number>(400);
  const [percent, setPercent] = React.useState<number | null>(null);
  const [isPercentEditorOpen, setIsPercentEditorOpen] = React.useState(false);
  const nomenclatureInfoT = useTranslation(NSKeys.nomenclatureInfo);
  const catalogsT = useTranslation(NSKeys.catalogs);
  const commonT = useTranslation(NSKeys.common);

  const dispatch = useLocalDispatch();
  const filterSearchString = useLocalSelector((state) => state.products.filterSearchString);
  const filterTags = useLocalSelector((state) => state.products.filterTags);
  const filterCategories = useLocalSelector((state) => state.products.filterCategories);
  const filterBrands = useLocalSelector((state) => state.products.filterBrands);

  const { data, isLoading } = useGetCatalogProductsQuery({
    q: filterSearchString,
    catalog_uid,
    apply_limits: false,
    only_positive_stock: true,
    category_id: filterCategories,
    tag_id: filterTags,
    brand_id: filterBrands,
  });
  const dataSource = data?.pages?.[0]?.items || [];

  React.useEffect(() => {
    const update = () => setY(window.innerHeight - 500);
    update();
    window.addEventListener('resize', update);
    return () => {
      dispatch(resetFilters());
      window.removeEventListener('resize', update);
    };
  }, []);

  React.useEffect(() => {
    if (!selectedProductUids.length) {
      setIsPercentEditorOpen(false);
    }
  }, [selectedProductUids.length]);

  const handleChange = (_keys: React.Key[], rows: ViewProductSchema[]) => {
    const currentLimitsByUid = Object.fromEntries(selectedProductUids.map((el) => [el.product_uid, el.limit_quantity]));
    const nextSelected = rows.map((row) => ({
      product_uid: row.uid,
      limit_quantity: currentLimitsByUid[row.uid] ?? 0,
    }));
    onSelectionChange?.(nextSelected);
  };

  const rowSelection: TableProps<ViewProductSchema>['rowSelection'] = {
    selectedRowKeys: selectedProductUids.map((el) => el.product_uid),
    onChange: handleChange,
  };

  const handleLimitChange = (productUid: string, value: number | null) => {
    const nextLimitQuantity = value ?? 0;
    const existing = selectedProductUids.find((el) => el.product_uid === productUid);

    if (existing) {
      onSelectionChange?.(
        selectedProductUids.map((el) =>
          el.product_uid === productUid ? { ...el, limit_quantity: nextLimitQuantity } : el
        )
      );
      return;
    }

    onSelectionChange?.([...selectedProductUids, { product_uid: productUid, limit_quantity: nextLimitQuantity }]);
  };

  const handleLimitBlur = (productUid: string, availableStock: number | null | undefined) => {
    const stock = Number(availableStock ?? 0);
    const existing = selectedProductUids.find((el) => el.product_uid === productUid);

    if (!existing) {
      return;
    }

    const normalizedLimit = Math.min(Math.max(existing.limit_quantity, 0), stock);

    if (normalizedLimit !== existing.limit_quantity) {
      onSelectionChange?.(
        selectedProductUids.map((el) =>
          el.product_uid === productUid ? { ...el, limit_quantity: normalizedLimit } : el
        )
      );
    }
  };

  const handleApplyPercent = () => {
    const productsByUid = Object.fromEntries(dataSource.map((product) => [product.uid, product]));

    onSelectionChange?.(
      selectedProductUids.map((selectedProduct) => {
        const availableStock = Number(productsByUid[selectedProduct.product_uid]?.available_stock ?? 0);
        return {
          ...selectedProduct,
          limit_quantity: Math.floor((availableStock * (percent ?? 0)) / 100),
        };
      })
    );

    setIsPercentEditorOpen(false);
  };

  return (
    <Container>
      <SearchControls maxTagCount="responsive" isLoading={isLoading} isViewSwitchVisible={false} />
      <AntTable<ViewProductSchema>
        columns={[
          ...BASE_COLUMNS.map((el) => ({
            ...el,
            title: nomenclatureInfoT.t(el.title as any),
            render: BASE_COLUMNS.find((baseCol) => baseCol.key === el.key)?.render,
          })),
          {
            title: (
              <Space size={4}>
                Лимит
                {selectedProductUids.length > 0 && (
                  <Tooltip title={catalogsT.t('limits.setPercent')}>
                    <Button
                      size="small"
                      type="link"
                      icon={<PercentageOutlined />}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsPercentEditorOpen((prev) => !prev);
                      }}
                    />
                  </Tooltip>
                )}
              </Space>
            ),
            dataIndex: 'limit_quantity',
            key: 'limit_quantity',
            className: 'limit-column',
            width: 120,
            render: (_, product) => (
              <InputNumber
                min={0}
                precision={0}
                value={selectedProductUids.find((el) => el.product_uid === product.uid)?.limit_quantity ?? 0}
                onChange={(value) => handleLimitChange(product.uid, value)}
                onBlur={() => handleLimitBlur(product.uid, product.available_stock)}
                onClick={(e) => e.stopPropagation()}
              />
            ),
          },
        ]}
        rowSelection={rowSelection}
        size="middle"
        key="uid"
        rowKey="uid"
        pagination={false}
        className="tableClassname"
        loading={isLoading}
        dataSource={dataSource}
        scroll={{ y }}
      />
      {isPercentEditorOpen && selectedProductUids.length > 0 && (
        <Space style={{ marginTop: 8 }}>
          <InputNumber min={0} max={100} precision={0} placeholder="%" value={percent} onChange={setPercent} />
          <Button size="small" type="primary" onClick={handleApplyPercent}>
            {commonT.t('buttonApply')}
          </Button>
        </Space>
      )}
    </Container>
  );
};
